import _get from 'lodash/get';

import RouteAccessCheck, {
  canOrder,
  supportedAddress,
  userAuthenticated,
  userUnauthenticated,
  unsupportedAddress,
  checkActiveOrder,
  redirectRoute,
} from 'core/api/routeAccessCheck';
import Tracker from 'core/api/tracking/tracker';

import { onrampSequenceForUser, ROUTE_MAP as SEQUENCE_MAP } from 'onramp/api/routeSequence';

import SuccessLayout from 'onramp/ui/layouts/V2/SuccessLayout';
import OnrampLayout from 'onramp/ui/layouts/OnrampLayout';
import OnrampLayoutV2 from 'onramp/ui/layouts/V2/OnrampLayout';
import OrderManagementLayout from 'onramp/ui/layouts/V2/OrderManagementLayout';
import SignInExistingUserPage from 'onramp/ui/pages/SignInExistingUserPage';
import SignUpPage from 'onramp/ui/pages/SignUpPage';
import UserManagementPage from 'onramp/ui/pages/V2/UserManagementPage';
import InitOrderPage from 'onramp/ui/pages/V2/InitOrderPage';
import EditWeekMenuPage from 'onramp/ui/pages/V2/EditWeekMenuPage';
import NewBillingInfoPage from 'onramp/ui/pages/V2/NewBillingInfoPage';
import ProgramConfirmationPage from 'onramp/ui/pages/V2/ProgramConfirmationPage';
import OutOfZonePage from 'onramp/ui/pages/OutOfZonePage';
import ProductDescriptionPage from 'onramp/ui/pages/V2/ProductDescriptionPage';
import MyOrderPage from 'onramp/ui/pages/V2/MyOrderPage';
import MyProgramPage from 'onramp/ui/pages/V2/MyProgramPage';
import SelectMealPage from 'onramp/ui/pages/V2/SelectMealPage';
import MySettingsPage from 'onramp/ui/pages/V2/MySettingsPage';
import ConfirmationPage from 'onramp/ui/pages/V2/ConfirmationPage';
import OrderHistoryPage from 'onramp/ui/pages/V2/OrderHistoryPage';
import MyInvoicesPage from 'onramp/ui/pages/V2/MyInvoicesPage';

export const canNotOrder = new RouteAccessCheck({
  check: (user, cache) => !canOrder.check(user, cache),
  pathname: '/',
  name: 'canNotOrder',
});

const onrampRedirect = new RouteAccessCheck({
  check: redirectRoute.check,
  pathname: '/onramp/init-order',
  name: 'onrampRedirect',
});

const newOnrampSignIn = new RouteAccessCheck({
  check: redirectRoute.check,
  pathname: '/sign-in',
  name: 'newOnrampSignIn',
});

const onrampUserUnauthenticated = new RouteAccessCheck({
  check: userUnauthenticated.check,
  pathname: '/onramp/init-order',
  name: 'onrampUserUnauthenticated',
});

const onrampSupportedAddress = new RouteAccessCheck({
  check: (user, cache) => {
    if (_get(user, 'primaryAddress.deliveryOption.adminOnly')) {
      return false;
    }

    return supportedAddress.check(user, cache);
  },
  pathname: SEQUENCE_MAP.outOfDeliveryZone.path,
  name: 'onrampSupportedAddress',
});

const onrampUnsupportedAddress = new RouteAccessCheck({
  check: (user, cache) => !onrampSupportedAddress.check(user, cache),
  pathname: unsupportedAddress.pathname,
  name: 'onrampUnsupportedAddress',
});

const onrampLoginCheck = new RouteAccessCheck({
  check: (user) => !(user && user.reorder),
  pathname: '/onramp/order-management',
  name: 'onrampLoginCheck',
});

const onrampAuthenitcatedCheck = new RouteAccessCheck({
  check: userAuthenticated.check,
  pathname: SEQUENCE_MAP.initOrder.path,
  name: 'onrampAuthenitcatedCheck',
});

// FIXME: the redirect pathname should be the first authenticated onramp route (not hardcoded to /onramp/about-you)
// Onramp route exists for user
export const onrampAccessCheck = (route) => (new RouteAccessCheck({
  check: (user, cache) => {
    const sequence = onrampSequenceForUser(user, cache);
    const currentIndex = sequence.findIndex((step) => step.name === route.name);
    /* eslint-disable-next-line no-console */
    console.log('check', sequence, currentIndex);
    if (currentIndex < 0) {
      return false; // route is not valid for the user
    }

    const prevSteps = sequence.slice(0, currentIndex);

    return prevSteps.every((step) => step.isComplete(user, cache));
  },
  pathname: SEQUENCE_MAP.initOrder.path,
  name: 'onrampAccessCheck',
}));
export const ROUTES = [
  {
    ...SEQUENCE_MAP.signIn, //This is used to sign in users who have not created any orders or passwords.
    component: SignInExistingUserPage,
    layout: OnrampLayout,
    layoutProps: {
      promoBannerEnabled: true,
      inverse: true,
      disableProgress: true,
    },
    accessChecks: [
      newOnrampSignIn,
    ],
  },
  {
    ...SEQUENCE_MAP.signUp,
    component: SignUpPage,
    layout: OnrampLayout,
    layoutProps: {
      promoBannerEnabled: true,
      inverse: true,
      disableProgress: true,
    },
    accessChecks: [
      onrampRedirect,
      onrampUserUnauthenticated,
    ],
  },
  {
    ...SEQUENCE_MAP.orderManagement,
    component: UserManagementPage,
    layout: OrderManagementLayout,
    layoutProps: {
      fullHeight: true,
    },
    accessChecks: [
      onrampLoginCheck,
      onrampAccessCheck(SEQUENCE_MAP.orderManagement),
    ],
  },
  {
    ...SEQUENCE_MAP.productDescription,
    component: ProductDescriptionPage,
    layout: OrderManagementLayout,
    layoutProps: {},
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.editWeekMenu,
    component: EditWeekMenuPage,
    layout: OrderManagementLayout,
    layoutProps: {
      fullHeight: true,
    },
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.selectMeal,
    component: SelectMealPage,
    layout: OrderManagementLayout,
    layoutProps: {
      fullHeight: true,
    },
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.myOrder,
    component: MyOrderPage,
    layout: OrderManagementLayout,
    layoutProps: {
      fullHeight: true,
    },
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.confirmation,
    component: ConfirmationPage,
    layout: OrderManagementLayout,
    layoutProps: {},
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.myProgram,
    component: MyProgramPage,
    layout: OrderManagementLayout,
    layoutProps: {},
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.mySettings,
    component: MySettingsPage,
    layout: OrderManagementLayout,
    layoutProps: {},
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.orderHistory,
    component: OrderHistoryPage,
    layout: OrderManagementLayout,
    layoutProps: {},
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.myInvoices,
    component: MyInvoicesPage,
    layout: OrderManagementLayout,
    layoutProps: {},
    accessChecks: [
      onrampAuthenitcatedCheck,
    ],
  },
  {
    ...SEQUENCE_MAP.initOrder,
    component: InitOrderPage,
    layout: OnrampLayoutV2,
    layoutProps: {
      promoBannerEnabled: true,
    },
    accessChecks: [
      canNotOrder,
      onrampAccessCheck(SEQUENCE_MAP.initOrder),
    ],
  },
  {
    ...SEQUENCE_MAP.newBillingInfo,
    component: NewBillingInfoPage,
    layout: OnrampLayoutV2,
    layoutProps: {
      promoBannerEnabled: true,
    },
    onLoad: () => Tracker.trackCheckoutProgress({ step: 2 }),
    accessChecks: [
      userAuthenticated,
      checkActiveOrder,
      canNotOrder,
      onrampSupportedAddress,
      onrampAccessCheck(SEQUENCE_MAP.newBillingInfo),
    ],
  },
  {
    ...SEQUENCE_MAP.prepaidConfirmation,
      component: ProgramConfirmationPage,
      layout: SuccessLayout,
      layoutProps: {
        promoBannerEnabled: true,
      },
      accessChecks: [
        userAuthenticated,
      ],
    },
  {
    ...SEQUENCE_MAP.outOfDeliveryZone,
    component: OutOfZonePage,
    layout: OnrampLayout,
    layoutProps: {
      disableProgress: true,
    },
    accessChecks: [
      userAuthenticated,
      onrampUnsupportedAddress,
    ],
  },
];

const ROUTE_MAP = ROUTES.reduce((obj, route) => ({ ...obj, [route.name]: route }), {});

export default ROUTE_MAP;
